
import {
  computed, defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import AppLoader from '@/components/UI/AppLoader.vue';
import AppTakePhoto from '@/components/AppTakePhoto.vue';
import AppSkeletonTakePhoto from '@/components/skeletons/AppSkeletonTakePhoto.vue';
import { IFileUpload, IImageConverted, IImageCrop } from '@/types/interfaces';
import { useMainStore } from '@/store/mainStore';
import { useFlutterBus } from '@/store/flutterBus';
import { UPLOAD_PLACES, CHOOSER_TYPE } from '@/types/enums';
import InputLabelsService from '@/services/InputLabelsService';
import { getVideoThumbnail, blobToUrl } from '@/helpers/videoThumbnail';
import UseUpload from '@/composables/UseUpload';
import UploadService from '@/services/UploadService';
import useFlutter from '@/composables/UseFlutter';
import FromFlutterService from '@/services/FromFlutterService';
import { bmpConvertor } from '@/helpers/bmpConvertor';
import ToFlutterService from '@/services/ToFlutterService';
import MonitoringService from '@/services/MonitoringService';

export default defineComponent({
  components: {
    AppLoader,
    // AppCropper,
    AppTakePhoto,
    // AppUploadInfo,
    AppSkeletonTakePhoto,
  },
  setup() {
    const mainStore = useMainStore();
    const flutterBus = useFlutterBus();

    const appTakePhoto = ref();

    const imageToCrop = ref({} as IImageCrop);

    const mediaInputRef: Ref<HTMLInputElement | null> = ref(null);
    const mediaLoadedKey = ref(0);
    const mediaToCrop = ref({} as IImageCrop);

    const isSaving: Ref<string | null> = ref(null);
    const currentChooserType = ref('');

    const { uploadInfo, onUploadUpdate } = UseUpload();

    const loaderText = computed(() => {
      if (isSaving.value) {
        return 'Updating media';
      }
      return '';
    });

    const router = useRouter();

    const isMediaProcessing = ref<boolean>(false);
    async function addMedia(e: any) {
      const isBmp = e.target.files ? e.target.files[0].type.includes('bmp') : false;
      const img: File = (e.target as HTMLInputElement).files![0]; // TODO fix files![]

      let poster = '' as unknown | undefined | string;
      let converted = {} as IImageConverted;

      if (isBmp) {
        converted = await bmpConvertor(e.target.files[0]);
      }

      if (!isBmp) {
        if (img.type.includes('video')) {
          isMediaProcessing.value = true;
          const thumbnailBlob = await getVideoThumbnail(img, 1.0);
          poster = blobToUrl(thumbnailBlob);
        }
        isMediaProcessing.value = false;
      }

      mediaToCrop.value = {
        url: isBmp ? converted.url : URL.createObjectURL(img),
        fileName: isBmp ? converted.name : img.name,
        fileType: isBmp ? converted.type : img.type,
        poster,
      };
      mediaLoadedKey.value += 1;
      mediaInputRef.value!.value = ''; // related to input that we know exactly exists
    }

    const { flutterOpenCamera, flutterAddMedia } = useFlutter();

    watch(() => flutterBus.flutterCameraFile, (newValue) => {
      if (newValue) {
        flutterAddMedia(newValue).then((r) => {
          mediaToCrop.value = r;
        });
        flutterBus.flutterCameraFile = null;
      }
    });

    function setChooserType(chooserType) {
      currentChooserType.value = chooserType;
      MonitoringService.logEvents({
        logType: 'setChooserType',
        userId: mainStore.user?.accountId,
        key: 'SET_CHOOSER_TYPE -- TOP',
        id: chooserType,
      });

      const { nativeApp } = window as any;

      if (nativeApp && chooserType) {
        MonitoringService.logEvents({
          logType: 'setChooserType',
          userId: mainStore.user?.accountId,
          key: 'SET_CHOOSER_TYPE -- INNER',
          id: chooserType,
        });
        nativeApp.setChooserType(chooserType);
      }
    }

    function handleMediaCancel() {
      mediaToCrop.value = {} as IImageCrop;
      appTakePhoto.value.resetAppTakePhoto();
    }

    const labels = computed(() => mainStore.recentInputLabels); // ref([] as ISearchLabelInput[]);
    async function handleLabels() {
      return InputLabelsService.getPersonalLabels().then((response) => {
        // labels.value = response.results.data;
        mainStore.recentInputLabels = response.results.data;
        return response;
      });
    }
    // quick fix to have labels list in create case from photo and add photo to case
    // refactor to proper way GL 8/8/2022
    const isAuth = computed(() => !!(mainStore.user.accountId));

    async function handleMultipleUpload(f: any) {
      const fileUploads = f.file as IFileUpload[];
      onUploadUpdate().setStartInfo(fileUploads);

      const folderId = mainStore.userSettings.accountSystemSettings.userUploadsFolderId;
      await handleLabels();
      UploadService.batchUploadTUS(fileUploads, onUploadUpdate, { location: UPLOAD_PLACES.FOLDER_PHOTO, id: folderId }, folderId, mainStore.recentInputLabels, true);
    }

    function handleRetake() {
      setChooserType(currentChooserType.value);
      mediaToCrop.value = {} as IImageCrop;
      (mediaInputRef!.value! as HTMLInputElement).click();
    }

    function createCase() {
      MonitoringService.logEvents({
        logType: 'createCaseHomeAndroid',
        userId: mainStore.user?.accountId,
        key: 'HOME_PAGE_CREATE_CASE_CLICK',
        id: '',
      });
      router.push('/drive/case/create?userFolder=true');
    }

    function cancelUpload() {
      isSaving.value = null;
      UploadService.isAbortUpload = true;
      handleMediaCancel();
    }

    onMounted(() => {
      if (isAuth.value) {
        handleLabels();
      }
    });

    return {
      mainStore,
      flutterBus,

      imageToCrop,
      isSaving,
      uploadInfo,

      mediaLoadedKey,
      mediaToCrop,
      mediaInputRef,
      addMedia,
      flutterAddMedia,
      flutterOpenCamera,
      handleMediaCancel,
      handleRetake,
      createCase,

      appTakePhoto,
      isMediaProcessing,
      handleMultipleUpload,
      // goToRegistration,
      labels,

      cancelUpload,
      setChooserType,
      loaderText,
      CHOOSER_TYPE,
    };
  },
});
